import { appThemeApiRef, configApiRef, useApi } from '@backstage/frontend-plugin-api';
import { SearchContextProvider } from '@backstage/plugin-search-react';
import { resolveGitHubContext } from '@vivicta/github-config';
import {
  AlertDialog,
  Button,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandShortcut,
} from '@vivicta/ui';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SignOutConfirmDialog } from '../settings/SignOutConfirmDialog';
import { PaletteSearchResults } from '../search/PaletteSearchResults';
import { templateTitle } from '../scaffolder/utils';
import { useTemplates } from '../scaffolder/useTemplates';
import { isTypingTarget } from '../../utils/isTypingTarget';
import { buildCommandEntries, buildCommandGroups, type CommandAction } from './commandRegistry';
import { ALL_NAV_ENTRIES } from './navEntries';

const isApplePlatform = (): boolean =>
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent);

/**
 * The portal's single search surface (T2.1–T2.3): one trigger, one dialog,
 * opened by ⌘K/Ctrl+K or `/`. It answers "jump to a page I know" from the
 * static Pages/Create/Actions registry and "find a thing I don't" from live
 * backend results, rather than splitting those across a sidebar link, a
 * separate modal and a search page. `/search` remains the deep-dive surface
 * for filters and full result sets, reachable from the Pages group and from
 * the "See all results" row.
 */
export const CommandPalette = () => {
  const navigate = useNavigate();
  const configApi = useApi(configApiRef);
  const appThemeApi = useApi(appThemeApiRef);
  const { templates } = useTemplates();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const [searching, setSearching] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const hasOpened = useRef(false);

  useEffect(() => {
    if (open) {
      hasOpened.current = true;
      return;
    }
    setQuery('');
    // `CommandDialog`'s Radix `Dialog` has no `Dialog.Trigger`, so Radix's own
    // close-focus restoration (which targets `context.triggerRef.current`) is
    // a no-op here and focus would otherwise fall through to `<body>`. Restore
    // it ourselves — but only after a real close, since this effect also runs
    // on mount, where it would put a focus ring on the trigger on every load.
    if (hasOpened.current) {
      triggerRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isPaletteKey =
        (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      // `/` is the conventional second shortcut for search-style palettes; it
      // only opens, never toggles, so typing it never closes the dialog.
      const isSearchKey = event.key === '/' && !event.metaKey && !event.ctrlKey;
      if (!isPaletteKey && !isSearchKey) return;
      if (isTypingTarget(event.target)) return;
      event.preventDefault();
      setOpen(current => (isSearchKey ? true : !current));
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const githubOrgUrl = useMemo(() => {
    const context = resolveGitHubContext(configApi);
    return `${context.webBaseUrl}/${context.organization}`;
  }, [configApi]);

  const entries = useMemo(
    () =>
      buildCommandEntries({
        navEntries: ALL_NAV_ENTRIES,
        templates: templates.map(template => ({
          namespace: template.metadata.namespace ?? 'default',
          name: template.metadata.name,
          title: templateTitle(template),
        })),
        githubOrgUrl,
      }),
    [templates, githubOrgUrl],
  );

  const groups = useMemo(() => buildCommandGroups(entries, query), [entries, query]);

  const runAction = (action: CommandAction) => {
    setOpen(false);
    switch (action.type) {
      case 'navigate':
        navigate(action.to);
        break;
      case 'toggle-theme':
        appThemeApi.setActiveThemeId(
          document.documentElement.classList.contains('dark')
            ? 'vivicta-light'
            : 'vivicta-dark',
        );
        break;
      case 'open-url':
        window.open(action.url, '_blank', 'noopener,noreferrer');
        break;
      case 'sign-out':
        setConfirmingSignOut(true);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        size="default"
        className="gap-2 font-normal text-muted-foreground"
        onClick={() => setOpen(true)}
      >
        <Search aria-hidden="true" className="size-4" />
        Search or jump to…
        <CommandShortcut className="ml-2">
          {isApplePlatform() ? '⌘K' : 'Ctrl K'}
        </CommandShortcut>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
        <CommandInput
          placeholder="Search the portal, jump to a page, or run an action…"
          value={query}
          onValueChange={setQuery}
        />
        {/* Fixed height, not content height: the groups shrink to nothing
          * between a keystroke and the debounced results landing, and a
          * centred dialog that resizes reads as flicker. */}
        <CommandList className="min-h-[300px]">
          <CommandEmpty>
            {searching ? 'Searching…' : 'No matches found.'}
          </CommandEmpty>
          {groups.map(group => (
            <CommandGroup key={group.group} heading={group.group}>
              {group.entries.map(entry => (
                <CommandItem
                  key={entry.id}
                  value={entry.id}
                  onSelect={() => runAction(entry.action)}
                >
                  {entry.label}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
          {/* Keyed on `open` so each visit starts from a clean search context
           * rather than replaying the previous session's term and results. */}
          <SearchContextProvider key={String(open)}>
            <PaletteSearchResults
              query={query}
              onDismiss={() => setOpen(false)}
              onPendingChange={setSearching}
            />
          </SearchContextProvider>
        </CommandList>
      </CommandDialog>

      <AlertDialog open={confirmingSignOut} onOpenChange={setConfirmingSignOut}>
        <SignOutConfirmDialog />
      </AlertDialog>
    </>
  );
};
